import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Autocomplete,
  Chip,
  IconButton,
} from "@mui/material";
import { Button } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";


interface IndustryTrackForm {
  industryName?: string;
  manager?: string;
  spoc1?: string;
  spoc2?: string;
  spoc3?: string;
  members?: string[]; 
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (payload: IndustryTrackForm) => Promise<void> | void;
  initialData?: IndustryTrackForm | null;
  membersOptions?: string[]; 
  title?: string;
  mode?: string;
  loading?: boolean;
  setLoading?: (loading: boolean) => void | undefined;
}

const IndustryTrackModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  initialData = null,
  membersOptions = [],
  title,
  mode,
  loading,
  setLoading,
}) => {
  const [form, setForm] = useState<IndustryTrackForm>({
    industryName: "",
    manager: "",
    spoc1: "",
    spoc2: "",
    spoc3: "",
    members: [],
  });
  const [memberInput, setMemberInput] = useState<string>("");

  useEffect(() => {
    if (initialData) {
      setForm({
        industryName:
          (initialData as any)["Industry Name"] ||
          initialData.industryName ||
          "",
        manager: (initialData as any)["Manager"] || initialData.manager || "",
        spoc1: (initialData as any)["SPOC 1"] || initialData.spoc1 || "",
        spoc2: (initialData as any)["SPOC 2"] || initialData.spoc2 || "",
        spoc3: (initialData as any)["SPOC 3"] || initialData.spoc3 || "",
        members: Array.isArray((initialData as any)["Members"])
          ? (initialData as any)["Members"]
          : typeof (initialData as any)["Members"] === "string"
            ? ((initialData as any)["Members"] as string).split(",").map((m: string) => m.trim())
            : initialData.members || [],
      });
    } else {
      setForm({
        industryName: "",
        manager: "",
        spoc1: "",
        spoc2: "",
        spoc3: "",
        members: [],
      });
    }
  }, [initialData, open]);

  const handleChange = (key: keyof IndustryTrackForm, value: any) => {
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleSave = async () => {
    // Basic validation: industry name required
    if (!form.industryName?.trim()) return;
    setLoading?.(true);
    try {
      await onSave(form);
      // onClose();
    } finally {
      setLoading?.(false);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{mode==="delete" ? "Are you sure want to delete this track" : title}</DialogTitle>
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "grey.600",
        }}
      >
        <CloseIcon />
      </IconButton>
      {mode !== "delete" && (
        <>
          <DialogContent dividers>
            <Box display="grid" gap={2} mt={1}>
              <TextField
                label="Industry Name"
                value={form.industryName}
                onChange={(e) => handleChange("industryName", e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Manager"
                value={form.manager}
                onChange={(e) => handleChange("manager", e.target.value)}
                fullWidth
              />
              <TextField
                label="SPOC 1"
                value={form.spoc1}
                onChange={(e) => handleChange("spoc1", e.target.value)}
                fullWidth
              />
              <TextField
                label="SPOC 2"
                value={form.spoc2}
                onChange={(e) => handleChange("spoc2", e.target.value)}
                fullWidth
              />
              <TextField
                label="SPOC 3"
                value={form.spoc3}
                onChange={(e) => handleChange("spoc3", e.target.value)}
                fullWidth
              />

              <Autocomplete
                multiple
                freeSolo
                filterSelectedOptions
                options={membersOptions || []}
                value={Array.isArray(form.members) ? form.members : []} // ensure array
                inputValue={memberInput}
                onInputChange={(_, newInput) => setMemberInput(newInput)}
                onChange={(_, value) => {
                  // Force value to array of strings and dedupe
                  const next = Array.isArray(value) ? value.map(String) : [String(value)];
                  const deduped = Array.from(
                    new Set(next.map((v) => v.trim()).filter(Boolean))
                  );
                  handleChange("members", deduped); // <-- now always an array
                  setMemberInput("");
                }}
                renderTags={(value: any[], getTagProps) =>
                  (Array.isArray(value) ? value : []).map((option: string, index) => (
                    <Chip label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Members"
                    placeholder="Add members"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const text: any = memberInput || params.inputProps?.value || "";
                        if (!text) return;
                        e.preventDefault();
                        const parts = text
                          .split(/[;,]\s*|\n/)
                          .map((p: any) => p.trim())
                          .filter(Boolean);
                        if (parts.length) {
                          const existing = Array.isArray(form.members) ? form.members : [];
                          const combined = Array.from(
                            new Set([...existing, ...parts])
                          );
                          handleChange("members", combined); // <-- always array
                          setMemberInput("");
                        }
                      }
                    }}
                  />
                )}
              />



            </Box>
          </DialogContent>
        </>
      )}

      <DialogActions>
        <Button onClick={onClose} disabled={loading} variant="outlined" color="neutral">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="solid" sx={{ backgroundColor: "#4D8FAC",  "&:hover": { backgroundColor: "#53a4c7ff" }, }} loading={loading} >
          {mode==="delete" ? "Delete" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndustryTrackModal;